import { Bike, Clock } from 'lucide-preact'
import { useState } from 'preact/hooks'
import { text } from '#contract'
import type { ShopSectionView } from '#contract'
import { durationText } from '../../../i18n/eta.ts'
import { useT } from '../../../i18n/index.ts'
import { ProductList } from './products.tsx'
import type { ProductEntry } from './products.tsx'
import styles from './products.module.css'
import { SectionCards, SectionPicture, SectionTiles } from './sections.tsx'
import { useShop } from './shop-context.ts'

/** The home page: a card per section, or aisles over every product (chips on the desktop). */
export function HomePage() {
  const t = useT()
  const { skin, os } = useShop()
  if (skin.home === 'cards') {
    return (
      <>
        <h2 class={styles.heading}>{t(skin.sectionsTitle)}</h2>
        <SectionCards />
      </>
    )
  }
  return os.form === 'desktop' ? <AisleGrid /> : <AisleHome />
}

/** Every product, each with its aisle's name. */
function useEverything(): readonly ProductEntry[] {
  const { vendor } = useShop()
  return vendor.sections.flatMap((section) =>
    section.products.map((product) => ({ product, detail: section.name })),
  )
}

/** The phone's aisles: tiles, then every product in rows. */
function AisleHome() {
  const t = useT()
  const { skin } = useShop()
  return (
    <>
      <h2 class={styles.heading}>{t(skin.sectionsTitle)}</h2>
      <SectionTiles />
      <h2 class={styles.heading}>{t(text('shop.everything'))}</h2>
      <ProductList entries={useEverything()} />
    </>
  )
}

/** The desktop's aisles: chips that filter one grid in place ("Everything" first). */
function AisleGrid() {
  const t = useT()
  const { vendor } = useShop()
  const everything = useEverything()
  const [aisle, setAisle] = useState<string | null>(null)
  const shown = vendor.sections.find((section) => section.id === aisle) ?? null
  const chips = [{ id: null, name: text('shop.everything') }, ...vendor.sections]
  return (
    <>
      <div class={styles.chips} role="group">
        {chips.map((chip) => (
          <button
            key={chip.id ?? ''}
            type="button"
            class={styles.chip}
            aria-pressed={chip.id === (shown?.id ?? null)}
            onClick={() => {
              setAisle(chip.id)
            }}
          >
            {t(chip.name)}
          </button>
        ))}
      </div>
      <ProductList
        key={shown?.id ?? ''}
        grid
        entries={shown ? shown.products.map((product) => ({ product })) : everything}
      />
    </>
  )
}

/**
 * A section's page: a restaurant (its dish large, tagline, time and fee, then the menu) or
 * an aisle (its products; a grid on the desktop).
 */
export function SectionPage({ section }: { readonly section: ShopSectionView }) {
  const t = useT()
  const { skin, vendor, money, os } = useShop()
  const entries = section.products.map((product) => ({ product }))
  if (skin.home === 'aisles') {
    return (
      <>
        <div class={styles.spacer} />
        <ProductList entries={entries} grid={os.form === 'desktop'} />
      </>
    )
  }
  return (
    <>
      <div class={styles.hero}>
        <SectionPicture section={section} />
        {section.tagline && <p class={styles.tagline}>{t(section.tagline)}</p>}
        <p class={styles.facts}>
          <span class={styles.fact}>
            <Clock aria-hidden="true" />
            {t(durationText(vendor.deliveryMinutes))}
          </span>
          <span class={styles.fact}>
            <Bike aria-hidden="true" />
            {t(text('shop.feeShort', { fee: money(vendor.feeCents) }))}
          </span>
        </p>
      </div>
      <h2 class={styles.heading}>{t(text('shop.menu'))}</h2>
      <ProductList entries={entries} />
    </>
  )
}
